/**
 * First-touch attribution. Click ids and UTMs are stored once.
 * Email addresses, auth codes, and tokens are never kept.
 */

import { ATTRIBUTION_RETENTION_MS, CLICK_ID_RETENTION_MS } from './cookie';

export const LEGACY_ATTRIBUTION_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export const REHUB_ATTRIBUTION_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'li_fat_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type LegacyAttributionParam = (typeof LEGACY_ATTRIBUTION_PARAMS)[number];
export type RehubAttributionParam = (typeof REHUB_ATTRIBUTION_PARAMS)[number];

export const PAID_MEDIUMS = [
  'cpc',
  'ppc',
  'sem',
  'cpm',
  'cpv',
  'cpp',
  'display',
  'paid',
  'paid_search',
  'paidsearch',
  'paid_social',
  'paidsocial',
] as const;

const CLICK_IDS = new Set(['gclid', 'gbraid', 'wbraid', 'fbclid', 'li_fat_id']);

const KNOWN_SOURCES: Record<string, string> = {
  google: 'Google',
  facebook: 'Facebook',
  fb: 'Facebook',
  instagram: 'Instagram',
  ig: 'Instagram',
  meta: 'Meta',
  bing: 'Bing',
  microsoft: 'Microsoft Ads',
  email: 'Email',
  newsletter: 'Email',
  linkedin: 'LinkedIn',
  twitter: 'X',
  x: 'X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  direct: 'Direct',
  organic: 'Organic',
};

const CARRY_PARAMS = ['landing_page', 'captured_at'] as const;

const STRIP_FROM_LANDING = new Set([
  'code',
  'access_token',
  'refresh_token',
  'token',
  'token_hash',
  'type',
  'error',
  'error_code',
  'error_description',
  'landing_page',
  'captured_at',
  'email',
  'user_email',
  'mail',
  'e',
]);

export interface AttributionStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type FirstTouchAttribution = {
  landing_page: string;
  captured_at: string;
} & Partial<Record<RehubAttributionParam, string>>;

export type AttributionPayload = Partial<Record<RehubAttributionParam, string>> & {
  landing_page?: string;
  captured_at?: string;
};

export interface AcquisitionFields {
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  fbclid?: string | null;
  li_fat_id?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
}

export interface ConsentGate {
  analytics: boolean;
  advertising: boolean;
}

function cleanValue(raw: string | null | undefined, max: number): string | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[\u0000-\u001F\u007F]/g, '').trim().slice(0, max);
  if (!cleaned || cleaned.includes('@')) return null;
  return cleaned;
}

function hasParam(touch: Partial<Record<string, string>>, params: readonly string[]): boolean {
  return params.some((key) => Boolean(touch[key]));
}

export function isPaidMedium(medium: string | null | undefined): boolean {
  if (!medium) return false;
  const normalised = medium.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!normalised) return false;
  if ((PAID_MEDIUMS as readonly string[]).includes(normalised)) return true;
  return normalised.startsWith('paid');
}

export function isGoogleAdsAttribution(input: AcquisitionFields): boolean {
  if (cleanValue(input.gclid, 200) || cleanValue(input.gbraid, 200) || cleanValue(input.wbraid, 200)) {
    return true;
  }
  const source = (input.utm_source || '').trim().toLowerCase();
  return source === 'google' && isPaidMedium(input.utm_medium);
}

function titleCaseSource(source: string): string {
  return source
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function acquisitionSourceLabel(input: AcquisitionFields): string {
  if (isGoogleAdsAttribution(input)) return 'Google Ads';
  const source = (input.utm_source || '').trim().toLowerCase();
  if (source) return KNOWN_SOURCES[source] || titleCaseSource(source);
  if (cleanValue(input.fbclid, 200)) return 'Meta';
  if (cleanValue(input.li_fat_id, 200)) return 'LinkedIn';
  if (
    cleanValue(input.utm_medium, 200) ||
    cleanValue(input.utm_campaign, 200) ||
    cleanValue(input.utm_content, 200) ||
    cleanValue(input.utm_term, 200)
  ) {
    return 'Other';
  }
  return 'Unknown';
}

function safeLandingPath(raw: string | null | undefined): string | null {
  const value = cleanValue(raw, 500);
  if (!value) return null;
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\') || value.includes('://')) {
    return null;
  }
  return value;
}

function landingFromLocation(pathname: string, search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  for (const key of [...params.keys()]) {
    const value = params.get(key) || '';
    if (STRIP_FROM_LANDING.has(key) || value.includes('@')) params.delete(key);
  }
  const qs = params.toString();
  const path = pathname && pathname.startsWith('/') ? pathname : '/';
  return `${path}${qs ? `?${qs}` : ''}`.slice(0, 500);
}

function parseCapturedAt(raw: string | null | undefined, nowIso: string): string | null {
  const value = cleanValue(raw, 40);
  if (!value) return null;
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return null;
  const now = Date.parse(nowIso);
  if (Number.isFinite(now) && parsed > now + 24 * 60 * 60 * 1000) return null;
  return new Date(parsed).toISOString();
}

function isCallbackPath(pathname: string): boolean {
  return pathname === '/auth/callback' || pathname === '/auth/callback/';
}

export function applyAttributionRetention<T extends FirstTouchAttribution>(touch: T, nowIso: string): T | null {
  const captured = Date.parse(touch.captured_at);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(captured) || !Number.isFinite(now)) return touch;
  const age = now - captured;
  if (age > ATTRIBUTION_RETENTION_MS) return null;
  if (age <= CLICK_ID_RETENTION_MS) return touch;
  const next = { ...touch };
  for (const key of CLICK_IDS) delete next[key as RehubAttributionParam];
  const stillHas = REHUB_ATTRIBUTION_PARAMS.some((key) => Boolean(next[key]));
  return stillHas ? next : null;
}

export interface FirstTouchTracker {
  readFirstTouch(storage: AttributionStore, now?: string): FirstTouchAttribution | null;
  captureFirstTouch(input: {
    pathname: string;
    search: string;
    storage: AttributionStore;
    now?: string;
  }): FirstTouchAttribution | null;
  authCallbackUrl(origin: string, touch: FirstTouchAttribution | null): string;
  attributionPayload(touch: FirstTouchAttribution | null): AttributionPayload | null;
  signupUserData(touch: FirstTouchAttribution | null): { acquisition?: AttributionPayload };
}

export function createFirstTouchTracker(options: {
  storageKey: string;
  params: readonly string[];
}): FirstTouchTracker {
  const params = options.params;

  function readFirstTouch(storage: AttributionStore, now?: string): FirstTouchAttribution | null {
    try {
      const raw = storage.getItem(options.storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<FirstTouchAttribution>;
      if (!parsed || typeof parsed !== 'object') return null;
      if (typeof parsed.captured_at !== 'string' || !parsed.captured_at) return null;
      const touch: FirstTouchAttribution = {
        landing_page: typeof parsed.landing_page === 'string' ? parsed.landing_page : '',
        captured_at: parsed.captured_at,
      };
      for (const key of params) {
        const value = cleanValue(parsed[key as RehubAttributionParam], 200);
        if (value) touch[key as RehubAttributionParam] = value;
      }
      if (!hasParam(touch, params)) return null;
      return now ? applyAttributionRetention(touch, now) : touch;
    } catch {
      return null;
    }
  }

  function captureFirstTouch(input: {
    pathname: string;
    search: string;
    storage: AttributionStore;
    now?: string;
  }): FirstTouchAttribution | null {
    const now = input.now ?? new Date().toISOString();
    const existing = readFirstTouch(input.storage, now);
    if (existing) return existing;

    const searchParams = new URLSearchParams(input.search.startsWith('?') ? input.search.slice(1) : input.search);
    const touch: FirstTouchAttribution = { landing_page: '', captured_at: '' };
    for (const key of params) {
      const value = cleanValue(searchParams.get(key), 200);
      if (value) touch[key as RehubAttributionParam] = value;
    }
    if (!hasParam(touch, params)) return null;

    const forwarded = isCallbackPath(input.pathname);
    touch.landing_page = (forwarded ? safeLandingPath(searchParams.get('landing_page')) : null)
      ?? landingFromLocation(input.pathname, input.search);
    touch.captured_at = (forwarded ? parseCapturedAt(searchParams.get('captured_at'), now) : null) ?? now;

    try {
      input.storage.setItem(options.storageKey, JSON.stringify(touch));
    } catch {
      /* storage unavailable */
    }
    return touch;
  }

  function authCallbackUrl(origin: string, touch: FirstTouchAttribution | null): string {
    const url = new URL('/auth/callback', origin);
    if (!touch) return url.toString();
    for (const key of params) {
      const value = touch[key as RehubAttributionParam];
      if (value) url.searchParams.set(key, value);
    }
    for (const key of CARRY_PARAMS) {
      const value = touch[key];
      if (value) url.searchParams.set(key, value);
    }
    return url.toString();
  }

  function attributionPayload(touch: FirstTouchAttribution | null): AttributionPayload | null {
    if (!touch || !hasParam(touch, params)) return null;
    const payload: AttributionPayload = {};
    for (const key of params) {
      const value = touch[key as RehubAttributionParam];
      if (value) payload[key as RehubAttributionParam] = value;
    }
    if (touch.landing_page) payload.landing_page = touch.landing_page;
    if (touch.captured_at) payload.captured_at = touch.captured_at;
    return payload;
  }

  function signupUserData(touch: FirstTouchAttribution | null): { acquisition?: AttributionPayload } {
    const acquisition = attributionPayload(touch);
    return acquisition ? { acquisition } : {};
  }

  return { readFirstTouch, captureFirstTouch, authCallbackUrl, attributionPayload, signupUserData };
}

export const legacyAttribution = createFirstTouchTracker({
  storageKey: 'photoclear_acquisition_first',
  params: LEGACY_ATTRIBUTION_PARAMS,
});

export const rehubAttribution = createFirstTouchTracker({
  storageKey: 'rehub_attr',
  params: REHUB_ATTRIBUTION_PARAMS,
});

/** Persist a first touch only after analytics or advertising consent. */
export function captureAttributionIfConsented(input: {
  pathname: string;
  search: string;
  storage: AttributionStore;
  consent: ConsentGate | null;
  now?: string;
}): FirstTouchAttribution | null {
  if (!input.consent?.analytics && !input.consent?.advertising) return null;
  return rehubAttribution.captureFirstTouch(input);
}
